import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TourSpotlight from './TourSpotlight';
import { TourContext } from './tourContext';
import { getTour, getToursForRoute, ALL_TOUR_IDS } from './tourContent';
import { waitForTourTarget } from './waitForTourTarget';

export { useTour } from './tourContext';

/**
 * Site-wide tour orchestrator.
 *
 * Owns the currently-running tour, its current beat index, completion
 * persistence (localStorage), auto-start gating for new users, and the
 * `<TourSpotlight>` overlay that renders the dim-backdrop + tooltip on top
 * of the app. Children consume it via `useTour()` to start tours from
 * trigger surfaces (HelpLauncher, per-section links, sidebar entry).
 *
 * Persistence: completion ids are stored in localStorage under
 * `txnweb_tours_completed`. Server-side migration is intentionally deferred
 * (see the parent task brief); switching to a server-backed list is a
 * single-call swap in `markCompleted` and `loadCompleted` here.
 *
 * Auto-start: when the user has not seen the welcome tour AND they're not
 * loading the app under `?notour=1`, the welcome tour fires once they're
 * on `/dashboard`. The flag persists immediately so reloads don't re-fire.
 *
 * Events dispatched on `window`:
 *   - `tour:started`   detail.tourId
 *   - `tour:completed` detail.tourId
 *   - `tour:skipped`   detail.tourId
 */

const COMPLETED_KEY = 'txnweb_tours_completed';
const AUTO_WELCOME_KEY = 'txnweb_tour_welcome_autorun';

function loadCompleted() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(COMPLETED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function saveCompleted(list) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COMPLETED_KEY, JSON.stringify(list));
  } catch {
    /* private mode — silently ignored */
  }
}

/** Read a URL param without bringing in extra router plumbing. */
function readSearchParam(name) {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

function emit(name, detail) {
  if (typeof window === 'undefined' || !window.dispatchEvent) return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* ignored — older browsers without CustomEvent are out of support */
  }
}

export function TourProvider({ children, user = null }) {
  const [activeTourId, setActiveTourId] = useState(null);
  /**
   * Two-phase step index:
   *   - `requestedStepIndex` is what the operator just asked for (Next /
   *     Back / a programmatic jump). It changes synchronously.
   *   - `stepIndex` is what Joyride is allowed to render — only commit
   *     after the step's target has actually mounted in the DOM.
   *
   * The split fixes the "skip to slide 26" cascade: chapter-boundary
   * steps in `wizard.full_walkthrough` flip the editor to a different
   * `wizardSection`, and that section is `React.lazy`-loaded. Without a
   * gate Joyride synchronously sees the new `stepIndex`, fails to find
   * the target inside the (still loading) section, fires
   * `TARGET_NOT_FOUND` → our handler advances → next step ALSO fails →
   * cascade until we land on the wrap-up `body` anchor.
   *
   * By holding the committed index back until `waitForTourTarget`
   * resolves, Joyride only ever sees a stepIndex whose target is
   * actually present, so the cascade can't start.
   */
  const [requestedStepIndex, setRequestedStepIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(() => loadCompleted());
  const navigate = useNavigate();
  const location = useLocation();
  const launcherToggleRef = useRef(null);

  const activeTour = useMemo(() => (activeTourId ? getTour(activeTourId) : null), [activeTourId]);

  /**
   * Per-step transition.
   *
   * When the operator advances/backs to a new step we:
   *   1. Fire `tour:wizard-section` immediately if the step opts into a
   *      wizard chapter (`ExperimentEditor.jsx` listens and flips the
   *      editor — that mount is async because each section is
   *      `React.lazy`-loaded).
   *   2. Run the step's optional `beforeShow` hook (best-effort).
   *   3. Wait for the step's target to land in the DOM (poll up to
   *      ~3.5s). Without this gate Joyride looks too early, fires
   *      `TARGET_NOT_FOUND` and our spotlight handler cascades through
   *      every following step until one resolves — that's the bug that
   *      shipped the "skip to slide 26" report.
   *   4. Commit `stepIndex` so Joyride paints the new tooltip.
   *
   * If the target really never shows we log a single warning and commit
   * anyway, so a stale catalogue selector can't strand the operator on
   * one beat. Joyride may then fire TARGET_NOT_FOUND once and our
   * handler advances by exactly one step (each transition runs through
   * this same wait, so a single missing selector cannot snowball).
   */
  useEffect(() => {
    if (!activeTour) return undefined;
    if (requestedStepIndex === stepIndex) return undefined;
    const step = activeTour.steps[requestedStepIndex];
    if (!step) return undefined;

    if (step.wizardSection) {
      emit('tour:wizard-section', { section: step.wizardSection });
    }
    if (typeof step.beforeShow === 'function') {
      try {
        step.beforeShow();
      } catch {
        /* tours must never crash the host UI — beforeShow is best-effort. */
      }
    }

    const wait = waitForTourTarget(step.target);
    let cancelled = false;
    wait.then((found) => {
      if (cancelled) return;
      if (!found && typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn(
          `[tour:${activeTour.id}] step "${step.id}" target ${step.target} did not mount within timeout; advancing anyway`
        );
      }
      setStepIndex(requestedStepIndex);
    });

    return () => {
      cancelled = true;
      wait.cancel();
    };
  }, [activeTour, requestedStepIndex, stepIndex]);

  const markCompleted = useCallback((tourId) => {
    setCompleted((prev) => {
      if (prev.includes(tourId)) return prev;
      const next = [...prev, tourId];
      saveCompleted(next);
      return next;
    });
  }, []);

  const startTour = useCallback(
    (tourId, { skipNav = false } = {}) => {
      const tour = getTour(tourId);
      if (!tour) return;
      setActiveTourId(tour.id);
      setRequestedStepIndex(0);
      setStepIndex(0);
      emit('tour:started', { tourId: tour.id });

      if (!skipNav && tour.route && typeof tour.route === 'string' && tour.route !== location.pathname) {
        navigate(tour.route);
      }
    },
    [navigate, location.pathname]
  );

  const end = useCallback(
    ({ outcome = 'completed' } = {}) => {
      if (!activeTourId) return;
      const id = activeTourId;
      if (outcome === 'completed') markCompleted(id);
      emit(outcome === 'skipped' ? 'tour:skipped' : 'tour:completed', { tourId: id });
      setActiveTourId(null);
      setRequestedStepIndex(0);
      setStepIndex(0);
      // Completing the welcome tour opens the HelpLauncher panel so the
      // operator immediately sees the full catalogue of follow-up tours
      // (matches the "Continue exploring →" promise in the brief).
      if (id === 'welcome' && outcome === 'completed') {
        // Defer a frame so the spotlight teardown finishes first.
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            if (launcherToggleRef.current) launcherToggleRef.current(true);
          }, 220);
        }
      }
    },
    [activeTourId, markCompleted]
  );

  const advance = useCallback(() => {
    if (!activeTour) return;
    setRequestedStepIndex((prev) => {
      const next = prev + 1;
      if (next >= activeTour.steps.length) {
        end({ outcome: 'completed' });
        return prev;
      }
      return next;
    });
  }, [activeTour, end]);

  const back = useCallback(() => {
    setRequestedStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const skip = useCallback(() => {
    end({ outcome: 'skipped' });
  }, [end]);

  /**
   * Open the welcome tour automatically the first time a user lands on the
   * dashboard. Gated:
   *   - localStorage must not already record the welcome tour as completed
   *   - The URL must not carry `?notour=1` (used by Playwright + dev tests)
   *   - The current user must not be flagged as an impersonation session
   *   - The auto-run sentinel hasn't fired in this browser yet
   *
   * Note: this fires once per browser, not per user — switching the
   * server-side `toursCompleted` field on the user model later will tighten
   * this to per-user.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname !== '/dashboard') return;
    if (completed.includes('welcome')) return;
    if (readSearchParam('notour') === '1') return;
    if (user && (user.impersonating || user.impersonatedBy)) return;

    let sentinel = null;
    try {
      sentinel = window.localStorage.getItem(AUTO_WELCOME_KEY);
    } catch {
      /* ignored */
    }
    if (sentinel === '1') return;

    try {
      window.localStorage.setItem(AUTO_WELCOME_KEY, '1');
    } catch {
      /* ignored */
    }

    // Defer slightly so the dashboard's hero finishes painting.
    const id = window.setTimeout(() => startTour('welcome', { skipNav: true }), 600);
    return () => window.clearTimeout(id);
  }, [location.pathname, completed, user, startTour]);

  const registerLauncherToggle = useCallback((fn) => {
    launcherToggleRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const openLauncher = useCallback(() => {
    if (launcherToggleRef.current) launcherToggleRef.current(true);
  }, []);

  const value = useMemo(
    () => ({
      currentTour: activeTour,
      currentStep: activeTour ? activeTour.steps[stepIndex] || null : null,
      stepIndex,
      isRunning: !!activeTour,
      startTour,
      advance,
      back,
      skip,
      end,
      completedIds: completed,
      isCompleted: (id) => completed.includes(id),
      allTourIds: ALL_TOUR_IDS,
      toursForRoute: () => getToursForRoute(location.pathname),
      registerLauncherToggle,
      openLauncher,
    }),
    [
      activeTour,
      stepIndex,
      startTour,
      advance,
      back,
      skip,
      end,
      completed,
      location.pathname,
      registerLauncherToggle,
      openLauncher,
    ]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourSpotlight />
    </TourContext.Provider>
  );
}
